import { api } from './index';
import type { Role } from './roles';

export type User = {
  id?: number;
  email: string;
  name?: string;
  // The name and RUT the user signs registros clínicos with; without them
  // (clinicalIdentity false) the server refuses their clinical writes.
  firstName?: string | null;
  lastName?: string | null;
  dni?: string | null;
  clinicalIdentity?: boolean;
  companyId?: number;
  companyName?: string;
  isActive: boolean;
  companyUsesIdCode: boolean;
  companyChargesAndPaysPerHours: boolean;
  companyChargesWithDateDetails: boolean;
  companyIncludeCheckedInShiftsOnly: boolean;
  companyAuditable: boolean;
  companyCaregiverDniRequired: boolean;
  companyShiftChangeRequestsEnabled: boolean;
  companyBonusFeatureEnabled: boolean;
  companyRoutePlansEnabled: boolean;
  companyCarePlanningEnabled: boolean;
  companyShiftImportsEnabled: boolean;
  companyGroupsEnabled: boolean;
  companyPatientServiceCategoriesEnabled: boolean;
  companyInventoryEnabled: boolean;
  // Caregivers are created, renamed and archived in the company's ERP.
  companyErpManagedCaregivers: boolean;
  companyMedicationsEnabled: boolean;
  // IANA zone ("America/Santiago"): dates the web app sends are read in it.
  companyTimeZone: string;
  // Kuido enabled billing for the company (gates every billing surface).
  companyIssuesTaxDocuments: boolean;
  // Enabled and connected to a provider: Cobros can emit.
  companyTaxDocumentsReady: boolean;
  // ISO 3166-1 alpha-2, lowercase; null when the company country has no code ("Otro").
  companyCountryCode: string | null;
  companyMapCenter: { lat: number; lng: number } | null;
  companyOptimizerWindowStart: string;
  companyOptimizerWindowEnd: string;
  companyOptimizerFixedStartOffset: number;
  companyOptimizerLunchBreakEnabled: boolean;
  grouped?: boolean;
  canManageGroups?: boolean;
  groupIds?: number[];
  roleId?: number | null;
  humanCreatedAt?: string;
  role?: Role;
}

export interface UserForm extends User {
  password: string;
  passwordConfirmation?: string;
  resetPasswordToken?: string;
}

export type SerializedUser = User;

export type UserIdentity = { firstName: string; lastName: string; dni: string };

const BASE_PATH = '/api/internal/users';

export const usersApi = {
  index(): Promise<User[]> {
    return api({
      method: 'get',
      url: BASE_PATH,
    }).then(response => response.data.users);
  },
  show(id: string | number): Promise<User> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => response.data.user);
  },
  logout() {
    return api({
      method: 'delete',
      url: '/users/sign_out',
    }).then(res => res.data);
  },
  login(user: Partial<UserForm>): Promise<SerializedUser> {
    return api({
      method: 'post',
      url: '/users/sign_in',
      data: { user },
    }).then(res => res.data.user);
  },
  recoverPassword(email: string) {
    return api({
      method: 'post',
      url: '/users/password',
      data: { user: { email } },
    }).then(res => res.data);
  },
  editPassword(user: Partial<UserForm>) {
    return api({
      method: 'put',
      url: '/users/password',
      data: { user },
    }).then(res => res.data);
  },
  update(id: string | number, user: Partial<User> & { roleId?: number }): Promise<User> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}`,
      data: { user },
    }).then(response => response.data.user);
  },
  createInternal(user: { email: string; roleId: number } & Partial<UserIdentity>): Promise<User> {
    return api({
      method: 'post',
      url: BASE_PATH,
      data: { user },
    }).then(response => response.data.user);
  },
  // The signed-in user's own name and RUT.
  updateProfile(identity: Partial<UserIdentity>): Promise<User> {
    return api({
      method: 'put',
      url: '/api/internal/profile',
      data: { user: identity },
    }).then(response => response.data.user);
  },
};
